import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useState } from "react";
import { UserContext } from "@/context/UserContext";
import LogoutModal from "@/components/Modal/LogoutModal";
import { getHRCredits, getTotalCredits } from "@/services/hrApi";
import { useEffect } from "react";

const menus = {
  instructor: [
    {
      icon: "feather-home",
      text: "My Dashboard",
      link: "/instructor/instructor-dashboard",
    },
    {
      icon: "feather-bookmark",
      text: "Bookmark",
      link: "/instructor/instructor-bookmark",
    },
    {
      icon: "feather-heart",
      text: "Wishlist",
      link: "/instructor/instructor-wishlist",
    },
    {
      icon: "feather-star",
      text: "Reviews",
      link: "/instructor/instructor-reviews",
    },
    {
      icon: "feather-help-circle",
      text: "My Quizzes",
      link: "/instructor/instructor-quiz-attempts",
    },
    {
      icon: "feather-volume-2",
      text: "Announcements",
      link: "/instructor/instructor-announcements",
    },
    {
      icon: "feather-book-open",
      text: "My Courses",
      link: "/instructor/instructor-personal-courses",
    },
    {
      icon: "feather-layers",
      text: "Library",
      link: "/instructor/instructor-library",
    },
    {
      icon: "feather-file-text",
      text: "Assignments",
      link: "/instructor/instructor-assignments",
    },
    {
      icon: "feather-settings",
      text: "Profile Settings",
      link: "/instructor/instructor-settings",
    },
  ],
  student: [
    {
      icon: "feather-home",
      text: "Student Dashboard",
      link: "/student/student-dashboard",
    },
    {
      icon: "feather-bookmark",
      text: "Bookmark",
      link: "/student/student-bookmark",
    },
    {
      icon: "feather-book-open",
      text: "Enrolled Courses",
      link: "/student/student-enrolled-course",
    },
    {
      icon: "feather-calendar",
      text: "Enrolled Events",
      link: "/student/student-enrolled-events",
    },
    {
      icon: "feather-heart",
      text: "Wishlist",
      link: "/student/student-wishlist",
    },
    { icon: "feather-star", text: "Reviews", link: "/student/student-reviews" },
    {
      icon: "feather-edit-3",
      text: "My Quiz Attempts",
      link: "/student/student-quiz-attempts",
    },
    {
      icon: "feather-layers",
      text: "Library",
      link: "/student/student-library",
    },
    {
      icon: "feather-briefcase",
      text: "My Jobs",
      link: "/student/student-applied-jobs",
    },
    {
      icon: "feather-settings",
      text: "Profile Settings",
      link: "/student/student-settings",
    },
  ],
  hr: [
    {
      icon: "feather-grid",
      text: "HR Dashboard",
      link: "/hr-admin/hr-dashboard",
    },
    { icon: "feather-users", text: "Students", link: "/candidates" },
    {
      icon: "feather-heart",
      text: "Wishlist",
      link: "/hr-admin/wishlisted-candidates",
    },
    {
      icon: "feather-volume-2",
      text: "My Announcements",
      link: "/hr-admin/hr-announcements",
    },
    { icon: "feather-briefcase", text: "My Jobs", link: "/hr-admin/hr-jobs" },
    {
      icon: "feather-message-circle",
      text: "My Enquiries",
      link: "/hr-admin/hr-enquiries",
    },
    {
      icon: "feather-file-text",
      text: "Resume Analysis",
      link: "/hr-admin/bulk-resume-analyzer",
    },
    { icon: "feather-layers", text: "Library", link: "/hr-admin/hr-library" },
    {
      icon: "feather-settings",
      text: "Profile Settings",
      link: "/hr-admin/hr-profile-settings",
    },
  ],
  admin: [
    {
      icon: "feather-grid",
      text: "Admin Dashboard",
      link: "/admins/admin-dashboard",
    },
    { icon: "feather-map", text: "Map View", link: "/admins/admin-map" },
    { icon: "feather-layers", text: "Category", link: "/admins/category" },
    {
      icon: "feather-check-circle",
      text: "Approvals",
      link: "/admins/approvals",
    },
    {
      icon: "feather-book-open",
      text: "Course Management",
      link: "/admins/Course",
    },
    { icon: "feather-briefcase", text: "Job Management", link: "/admins/Jobs" },
    {
      icon: "feather-calendar",
      text: "Event Management",
      link: "/admins/Events",
    },
    {
      link: "/admins/bookings",
      text: "Bookings",
      icon: "feather-bookmark",
    },
    {
      icon: "feather-bell",
      text: "Notifications",
      link: "/admins/notifications",
    },
    {
      icon: "feather-settings",
      text: "Settings",
      link: "/admins/admin-settings",
    },
  ],
};

const profileRoutes = {
  student: "/student/student-profile",
  instructor: "/instructor/instructor-profile",
  hr: "/hr-admin/hr-profile",
  admin: "/admins/admin-profile",
};

const User = ({ showProfile = true, showLogout = true, isMobile = false }) => {
  const router = useRouter();
  const { user, logoutUser } = useContext(UserContext);
  const [showConfirm, setShowConfirm] = useState(false);
  const [creditData, setCreditData] = useState({ available: user?.credits || 0, total: 0 });

  useEffect(() => {
    const fetchCredits = async () => {
      if (user?._id && user?.role === "hr") {
        try {
          const available = await getHRCredits(user._id);
          const total = await getTotalCredits(user._id);
          console.log("Credit update:", { available, total });
          setCreditData({
            available: available || 0,
            total: total || 0 // Default to 1000 if 0 or unavailable
          });
        } catch (error) {
          console.error("Error fetching credits:", error);
        }
      }
    };

    fetchCredits();
    // Refresh every 30 seconds to keep it updated while the menu is open
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [user]);

  console.log("User in User menu:", user);

  const handleLogoutClick = (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const handleConfirmLogout = async () => {
    await logoutUser();

    let redirectUrl = "/login";
    if (user?.role === "hr") {
      redirectUrl = "/login?role=hr";
    } else if (user?.role === "instructor") {
      redirectUrl = "/login?role=instructor";
    } else if (user?.role === "admin") {
      redirectUrl = "/login?role=admin";
    }

    setShowConfirm(false);
    router.push(redirectUrl);
  };

  const handleCancelLogout = () => {
    setShowConfirm(false);
  };

  if (!user) return null;

  const role = user.role || "student";
  const menuItems = menus[role] || [];
  const profileRoute = profileRoutes[role] || "/student/student-profile";

  return (
    <>
      {showConfirm && (
        <LogoutModal
          handleLogout={handleConfirmLogout}
          handleCancelLogout={handleCancelLogout}
        />
      )}

      <div
        className="rbt-user-menu-list-wrapper"
        style={!isMobile ? { right: "-70px", left: "auto" } : {}}
      >
        <div className="inner">
          {showProfile && (
            <div className="rbt-admin-profile">
              <div className="admin-thumbnail">
                <Image
                  src={
                    user.photoUrl
                      ? user.photoUrl
                      : user.gender === "Male"
                        ? "/images/instructor/instructor2.jpg"
                        : user.gender === "female"
                          ? "/images/instructor/instructor1.jpg"
                          : "/images/team/avatar.jpg"
                  }
                  width={43}
                  height={43}
                  alt="User"
                />
              </div>
              <div className="admin-info">
                <span className="name">
                  {(user?.firstName && user?.lastName
                    ? user.firstName + " " + user.lastName
                    : user?.userName
                      ? user.userName
                      : user?.firstName
                        ? user.firstName
                        : user?.Name
                          ? user?.Name
                          : user?.companyName
                  )?.replace(/^\w/, (c) => c.toUpperCase()) || "User"}
                </span>
                <Link
                  className="rbt-btn-link color-primary"
                  href={profileRoute}
                >
                  View Profile
                </Link>
              </div>
            </div>
          )}

          {user?.role === "hr" && (
            <div className="user-credit-card-wrapper mb--20 mt--5">
              <div className={`user-credit-card-compact ${creditData.available < (creditData.total * 0.1) ? 'low-balance' : ''}`}>
                <div className="card-header-flex">
                  <span className="compact-label">
                    {creditData.available < (creditData.total * 0.1) ? '⚠️ Low Balance' : 'Available Credits'}
                  </span>
                  <div className="count-right-small">
                    <span className="val">{creditData.available.toLocaleString()}</span>
                    <span className="max">/ {creditData.total.toLocaleString()}</span>
                  </div>
                </div>

                <div className="progress-thin-wrapper">
                  <div
                    className="progress-fill-sleek"
                    style={{
                      width: `${Math.min((creditData.available / (creditData.total || 1)) * 100, 100)}%`,
                      background: creditData.available < (creditData.total * 0.1) ? '#ff4d4f' : '#fff'
                    }}
                  ></div>
                </div>

                <div className="card-footer-flex">
                  <div className="usage-indicator">
                    <span className={`dot ${creditData.available < (creditData.total * 0.1) ? 'warning' : ''}`}></span>
                    
                    {creditData.available ? Math.max(0, 100 - ((creditData.available / (creditData.total || 1)) * 100)).toFixed(0) : "0"}% Used
                  </div>
                  <button
                    className="modern-upgrade-btn"
                    onClick={() => router.push("/credits")}
                  >
                    <i className="feather-zap"></i>
                    <span>Upgrade</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <ul className="user-list-wrapper">
            {menuItems.map((item, index) => (
              <li key={index}>
                <Link href={item.link}>
                  <i className={item.icon}></i>
                  <span>{item.text}</span>
                </Link>
              </li>
            ))}
          </ul>

          {showLogout && (
            <>
              <hr className="mt--10 mb--10" />
              <ul className="user-list-wrapper">
                <li>
                  <Link href="#" onClick={handleLogoutClick}>
                    <i className="feather-log-out"></i>
                    <span>Logout</span>
                  </Link>
                </li>
              </ul>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .user-credit-card-wrapper {
          padding: 0 10px;
        }
        .user-credit-card-compact {
          background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 15px 35px rgba(99, 102, 241, 0.3);
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.4s ease;
        }
        .user-credit-card-compact.low-balance {
          background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
          box-shadow: 0 15px 35px rgba(239, 68, 68, 0.3);
          border-color: rgba(255, 255, 255, 0.3);
        }
        .user-credit-card-compact::after {
          content: "";
          position: absolute;
          top: -20px;
          left: -20px;
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          pointer-events: none;
        }
        .card-header-flex {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          position: relative;
          z-index: 1;
        }
        .compact-label {
          font-size: 10px;
          font-weight: 900;
          color: rgba(255, 255, 255, 0.9);
          text-transform: uppercase;
          letter-spacing: 1px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        .count-right-small {
          display: flex;
          align-items: baseline;
          gap: 2px;
          background: rgba(0, 0, 0, 0.15);
          padding: 3px 10px;
          border-radius: 20px;
          backdrop-filter: blur(4px);
        }
        .count-right-small .val {
          color: #fff;
          font-size: 14px;
          font-weight: 800;
        }
        .count-right-small .max {
          color: rgba(255, 255, 255, 0.6);
          font-size: 10px;
          font-weight: 600;
        }
        .progress-thin-wrapper {
          height: 6px;
          background: rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          margin-bottom: 15px;
          overflow: hidden;
          position: relative;
          z-index: 1;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .progress-fill-sleek {
          height: 100%;
          background: #fff;
          border-radius: 10px;
          transition: width 1s cubic-bezier(0.19, 1, 0.22, 1);
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
        }
        .card-footer-flex {
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          z-index: 1;
        }
        .usage-indicator {
          font-size: 10px;
          color: #fff;
          font-weight: 800;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .usage-indicator .dot {
          width: 6px;
          height: 6px;
          background: #4ade80;
          border-radius: 50%;
          box-shadow: 0 0 12px #4ade80;
          animation: pulse 2s infinite;
        }
        .usage-indicator .dot.warning {
          background: #fbbf24;
          box-shadow: 0 0 12px #fbbf24;
        }
        @keyframes pulse {
          0% { transform: scale(0.9); opacity: 0.8; }
          70% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(0.9); opacity: 0.8; }
        }
        .modern-upgrade-btn {
          background: #fff;
          color: #6366f1;
          border: none;
          padding: 8px 18px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 800;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          letter-spacing: 0.5px;
        }
        .modern-upgrade-btn:hover {
          background: #f8fafc;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(255, 255, 255, 0.2);
          color: #4338ca;
        }
        .low-balance .modern-upgrade-btn {
          color: #ef4444;
        }
        .modern-upgrade-btn i {
          font-size: 14px;
          color: #8b5cf6;
          transition: transform 0.3s ease;
        }
        .low-balance .modern-upgrade-btn i {
          color: #f59e0b;
        }
        .modern-upgrade-btn:hover i {
          transform: rotate(15deg) scale(1.1);
        }
      `}</style>
    </>
  );
};

export default User;
